import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../../components/argus/AppShell";
import { Input } from "../../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Bug, Search, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import SeverityChip from "../../components/argus/SeverityChip";
import FindingDrawer from "../../components/argus/FindingDrawer";

const PAGE_SIZE = 50;

export default function Findings() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [severity, setSeverity] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scanner, setScanner] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [owaspFilter, setOwaspFilter] = useState("all");
  const [projects, setProjects] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Honour ?owasp=Axx query param from the Dashboard donut deep link
  useEffect(() => {
    const o = searchParams.get("owasp");
    if (o) {
      setOwaspFilter(o);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load projects once for the filter dropdown
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  // Debounce search input → server query
  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  // Reset offset when filters change
  useEffect(() => { setOffset(0); }, [qDebounced, severity, statusFilter, scanner, projectFilter, owaspFilter]);

  // Bump this to manually re-trigger the fetch from FindingDrawer.onUpdated etc.
  const [refreshTick, setRefreshTick] = useState(0);
  const refresh = () => setRefreshTick((t) => t + 1);

  // Fetch with a stale-response guard: if dependencies change while a request
  // is in flight, the older response is ignored when it eventually returns.
  useEffect(() => {
    let ignore = false;
    const params = { limit: PAGE_SIZE, offset };
    if (severity !== "all") params.severity = severity;
    if (statusFilter !== "all") params.status_filter = statusFilter;
    if (scanner !== "all") params.scanner = scanner;
    if (projectFilter !== "all") params.project_id = projectFilter;
    if (owaspFilter !== "all") params.owasp = owaspFilter;
    if (qDebounced.trim()) params.q = qDebounced.trim();
    setLoading(true);
    api.get("/findings", { params })
      .then((r) => {
        if (ignore) return;
        if (Array.isArray(r.data)) {
          setItems(r.data); setTotal(r.data.length);
        } else {
          setItems(r.data.items || []); setTotal(r.data.total || 0);
        }
      })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [qDebounced, severity, statusFilter, scanner, projectFilter, owaspFilter, offset, refreshTick]);

  const clearAll = () => {
    setQ(""); setSeverity("all"); setStatusFilter("all"); setScanner("all"); setProjectFilter("all"); setOwaspFilter("all");
    setSearchParams({});
  };

  const hasFilters = qDebounced || severity !== "all" || statusFilter !== "all" || scanner !== "all" || projectFilter !== "all" || owaspFilter !== "all";
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AppShell title="Findings">
      <div className="flex flex-wrap gap-3 mb-4" data-testid="findings-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, asset, CWE…" className="h-10 pl-9 bg-paper border-line" data-testid="findings-search" />
        </div>
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-10 w-40 bg-paper border-line" data-testid="findings-severity-filter"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="info">Info</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 w-40 bg-paper border-line" data-testid="findings-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="triaged">Triaged</SelectItem>
            <SelectItem value="fixed">Fixed</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="false_positive">False positive</SelectItem>
          </SelectContent>
        </Select>
        <Select value={scanner} onValueChange={setScanner}>
          <SelectTrigger className="h-10 w-36 bg-paper border-line" data-testid="findings-scanner-filter"><SelectValue placeholder="Scanner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scanners</SelectItem>
            <SelectItem value="sast">SAST</SelectItem>
            <SelectItem value="dast">DAST</SelectItem>
            <SelectItem value="dast-zap">DAST (ZAP)</SelectItem>
            <SelectItem value="sca">SCA</SelectItem>
            <SelectItem value="cloud-aws-s3">Cloud (AWS S3)</SelectItem>
          </SelectContent>
        </Select>
        <Select value={owaspFilter} onValueChange={setOwaspFilter}>
          <SelectTrigger className="h-10 w-44 bg-paper border-line" data-testid="findings-owasp-filter"><SelectValue placeholder="OWASP" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All OWASP 2025</SelectItem>
            <SelectItem value="A01">A01 — Broken Access Control</SelectItem>
            <SelectItem value="A02">A02 — Security Misconfiguration</SelectItem>
            <SelectItem value="A03">A03 — Software Supply Chain</SelectItem>
            <SelectItem value="A04">A04 — Cryptographic Failures</SelectItem>
            <SelectItem value="A05">A05 — Injection</SelectItem>
            <SelectItem value="A06">A06 — Insecure Design</SelectItem>
            <SelectItem value="A07">A07 — Authentication Failures</SelectItem>
            <SelectItem value="A08">A08 — Software/Data Integrity</SelectItem>
            <SelectItem value="A09">A09 — Logging & Alerting</SelectItem>
            <SelectItem value="A10">A10 — Exceptional Conditions</SelectItem>
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="h-10 w-48 bg-paper border-line" data-testid="findings-project-filter"><SelectValue placeholder="Project" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All projects</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="outline" onClick={clearAll} className="h-10 border-line bg-transparent text-ink/70 hover:text-ink" data-testid="findings-clear-filters">
            <X size={14} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between mb-3 text-xs text-ink/60">
        <span data-testid="findings-count">{loading ? <span className="inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Loading…</span> : `${total.toLocaleString()} finding${total === 1 ? "" : "s"}`}</span>
        {pages > 1 && (
          <div className="flex items-center gap-2" data-testid="findings-pagination">
            <Button size="sm" variant="outline" className="h-7 border-line bg-transparent" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} data-testid="findings-prev"><ChevronLeft size={12} /></Button>
            <span className="font-mono">Page {page} / {pages}</span>
            <Button size="sm" variant="outline" className="h-7 border-line bg-transparent" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)} data-testid="findings-next"><ChevronRight size={12} /></Button>
          </div>
        )}
      </div>

      {items.length === 0 && !loading ? (
        <div className="rounded-xl border border-dashed border-line p-12 text-center" data-testid="findings-empty">
          <Bug size={28} className="mx-auto text-ink/40 mb-3" />
          <p className="text-ink/70">No findings match these filters. Try widening them.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-line bg-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider sticky top-0">
              <tr>
                <th className="text-left px-4 py-2.5">Severity</th>
                <th className="text-left px-4 py-2.5">Title</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Asset</th>
                <th className="text-left px-4 py-2.5 hidden lg:table-cell">Scanner</th>
                <th className="text-left px-4 py-2.5 hidden lg:table-cell">CWE</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody data-testid="findings-table-body">
              {items.map((f) => (
                <tr key={f.id} className="border-t border-line hover:bg-raised/40 cursor-pointer dense-row" onClick={() => setActive(f)} data-testid={`finding-row-${f.id}`}>
                  <td className="px-4"><SeverityChip severity={f.severity} /></td>
                  <td className="px-4 max-w-md truncate">{f.title}</td>
                  <td className="px-4 font-mono text-xs hidden md:table-cell truncate max-w-[200px]">{f.asset}</td>
                  <td className="px-4 uppercase text-xs hidden lg:table-cell text-ink/70">{f.scanner}</td>
                  <td className="px-4 font-mono text-xs hidden lg:table-cell">{f.cwe_id || "—"}</td>
                  <td className="px-4 text-xs">{f.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FindingDrawer finding={active} open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }} onUpdated={refresh} />
    </AppShell>
  );
}
